import { all, call, put, select, takeEvery } from 'typed-redux-saga';

import { waitForAndGetFlagValuesSaga } from '@/core/flags/sagas/waitForAndGetFlagValues.saga';
import logger from '@/core/observability/logger';
import { pageTypeSelector } from '@/core/page/page.selectors';
import { getOrCreateVisitorId } from '@/core/tracking/utils/visitorId';
import { PartnerAdSpace } from '@/generated/graphql/types';
import { BannerCampaign } from '@/productDiscovery/CommercialAnimation/interfaces/bannerCampaign';
import { fetchListingCampaignsGql } from '@/productDiscovery/CommercialAnimation/services/fetchListingCampaigns';
import { abTestAndFeatureFlagValidated } from '@/productDiscovery/CommercialAnimation/utils/abTestAndFeatureFlagValidated';
import { getUserSegments } from '@/productDiscovery/CommercialAnimation/utils/getUserSegments';
import { selectQuery } from '@/productDiscovery/Listings/pages/Search/selectors/selectQuery';
import { ENABLE_PARTNER_AD_LISTING_CAMPAIGNS } from '@/sellerAdsManagement/PartnerAdBannerCampaign/constants';
import { PartnerAdBannerCampaign } from '@/sellerAdsManagement/PartnerAdBannerCampaign/interfaces/partnerAdBannerCampaign';
import {
  fetchPartnerAdCampaignsListingMiddleCategory,
  fetchPartnerAdCampaignsListingMiddleSearch,
} from '@/sellerAdsManagement/PartnerAdBannerCampaign/services/fetchPartnerAdListingCampaigns';
import { mapPartnerAdBannerCampaignToBannerCampaign } from '@/sellerAdsManagement/PartnerAdBannerCampaign/services/mapper';

import {
  fetchingMerchandizingBanners,
  fetchMerchandizingBannerFailed,
  fetchMerchandizingBannersSuccess,
  INIT_MERCHANDIZING_BANNERS,
  type InitMerchandizingBanners,
} from './MerchandizingBanners.actions';

function* getMerchandizingBanner(action: InitMerchandizingBanners) {
  yield put(fetchingMerchandizingBanners(action.bannerType));

  try {
    const flags = yield* call(waitForAndGetFlagValuesSaga);
    const userSegments = yield* call(getUserSegments);
    const pageType = yield* select(pageTypeSelector);
    const query = yield* select(selectQuery);
    const visitorId = yield* call(getOrCreateVisitorId);
    const fetchPartnerAdCampaignsEnabled = Boolean(
      flags[ENABLE_PARTNER_AD_LISTING_CAMPAIGNS] &&
        action.bannerType === 'middle',
    );
    let validCampaignBanners: BannerCampaign[] = [];

    let partnersType: PartnerAdSpace | null = null;
    if (
      fetchPartnerAdCampaignsEnabled &&
      (pageType === 'category_listing' ||
        pageType === 'model_listing' ||
        pageType === 'mass_landing') &&
      action.categoryIds &&
      action.categoryIds !== ''
    ) {
      partnersType = PartnerAdSpace.LISTING_MIDDLE_CATEGORY;
    } else if (
      fetchPartnerAdCampaignsEnabled &&
      pageType === 'search' &&
      query &&
      query !== ''
    ) {
      partnersType = PartnerAdSpace.LISTING_MIDDLE_SEARCH;
    }

    const {
      processedListingBannerCampaigns,
      processedPartnerAdListingBannerCampaigns,
    } = yield* all({
      processedListingBannerCampaigns: call(
        fetchListingCampaignsGql,
        action.bannerType,
        action.categoryIds,
        userSegments,
      ),
      processedPartnerAdListingBannerCampaigns:
        partnersType === PartnerAdSpace.LISTING_MIDDLE_CATEGORY
          ? call(
              fetchPartnerAdCampaignsListingMiddleCategory,
              action.categoryIds,
              visitorId,
            )
          : partnersType === PartnerAdSpace.LISTING_MIDDLE_SEARCH && query
            ? call(fetchPartnerAdCampaignsListingMiddleSearch, query, visitorId)
            : call(() => [] as PartnerAdBannerCampaign[]),
    });

    if (processedPartnerAdListingBannerCampaigns.length && partnersType) {
      validCampaignBanners = mapPartnerAdBannerCampaignToBannerCampaign(
        processedPartnerAdListingBannerCampaigns,
        partnersType,
      );
    } else {
      validCampaignBanners =
        processedListingBannerCampaigns &&
        processedListingBannerCampaigns.length > 0
          ? processedListingBannerCampaigns.filter((camp) =>
              abTestAndFeatureFlagValidated(camp, flags),
            )
          : [];
    }

    yield put(
      fetchMerchandizingBannersSuccess(action.bannerType, validCampaignBanners),
    );
  } catch (error) {
    logger.error(error);
    yield put(fetchMerchandizingBannerFailed(action.bannerType));
  }
}

export function* rootSaga(): Generator {
  yield takeEvery(INIT_MERCHANDIZING_BANNERS, getMerchandizingBanner);
}
